if (!customElements.get('cinematic-reel')) {
  class CinematicReel extends HTMLElement {
    constructor() {
      super();

      this.track = null;
      this.prevButton = null;
      this.nextButton = null;
      this.playButton = null;
      this.backdrop = null;
      this.closeButton = null;

      this.frame = null;
      this.speed = 0.24;
      this.resumeAt = 0;
      this.autoRemainder = 0;
      this.loopPoint = 0;
      this.initialized = false;

      this.autoplay = true;
      this.enableExpansion = false;
      this.pauseOnHover = true;
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.reducedMotion = false;

      this.isDragging = false;
      this.isHorizontalDrag = false;
      this.dragMoved = false;
      this.expandedSlide = null;
      this.lastFocusedElement = null;

      this.startX = 0;
      this.startY = 0;
      this.startScrollLeft = 0;

      this.boundOnWheel = this.onWheel.bind(this);
      this.boundOnPointerDown = this.onPointerDown.bind(this);
      this.boundOnPointerMove = this.onPointerMove.bind(this);
      this.boundOnPointerUp = this.onPointerUp.bind(this);
      this.boundOnPrevClick = this.onPrevClick.bind(this);
      this.boundOnNextClick = this.onNextClick.bind(this);
      this.boundOnPlayClick = this.onPlayClick.bind(this);
      this.boundOnSlideClick = this.onSlideClick.bind(this);
      this.boundOnBackdropClick = this.collapseSlide.bind(this);
      this.boundOnCloseClick = this.collapseSlide.bind(this);
      this.boundOnKeydown = this.onKeydown.bind(this);
      this.boundOnScroll = this.updateButtons.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnMouseEnter = this.onMouseEnter.bind(this);
      this.boundOnMouseLeave = this.onMouseLeave.bind(this);
      this.boundOnFocusIn = this.onFocusIn.bind(this);
      this.boundOnFocusOut = this.onFocusOut.bind(this);
      this.boundUpdateMetrics = this.queueMetricsUpdate.bind(this);
    }

    static get observedAttributes() {
      return ['data-reel-pace', 'data-reel-autoplay', 'data-reel-pause-on-hover', 'data-reel-expand'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      if (!['data-reel-pace', 'data-reel-autoplay', 'data-reel-pause-on-hover', 'data-reel-expand'].includes(name)) return;

      this.setupOptions();
      this.setupPace();
      this.setPlayButtonState();

      if (this.track && this.isConnected) {
        this.resumeAt = performance.now() + 150;
        this.startAutoScroll();
      }
    }

    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-reel-track]');
      this.prevButton = this.querySelector('[data-reel-prev]');
      this.nextButton = this.querySelector('[data-reel-next]');
      this.playButton = this.querySelector('[data-reel-play]');
      this.backdrop = this.querySelector('[data-reel-backdrop]');
      this.closeButton = this.querySelector('[data-reel-close]');

      if (!this.track) return;

      this.setupOptions();
      this.setupPace();
      this.setupLoop();
      this.watchMedia();

      this.track.addEventListener('wheel', this.boundOnWheel, { passive: false });
      this.track.addEventListener('pointerdown', this.boundOnPointerDown);
      this.track.addEventListener('scroll', this.boundOnScroll, { passive: true });

      window.addEventListener('pointermove', this.boundOnPointerMove, { passive: false });
      window.addEventListener('pointerup', this.boundOnPointerUp);
      window.addEventListener('pointercancel', this.boundOnPointerUp);
      window.addEventListener('resize', this.boundOnResize);

      this.prevButton?.addEventListener('click', this.boundOnPrevClick);
      this.nextButton?.addEventListener('click', this.boundOnNextClick);
      this.playButton?.addEventListener('click', this.boundOnPlayClick);

      this.addEventListener('click', this.boundOnSlideClick);
      this.addEventListener('keydown', this.boundOnKeydown);
      this.addEventListener('mouseenter', this.boundOnMouseEnter);
      this.addEventListener('mouseleave', this.boundOnMouseLeave);
      this.addEventListener('focusin', this.boundOnFocusIn);
      this.addEventListener('focusout', this.boundOnFocusOut);
      this.backdrop?.addEventListener('click', this.boundOnBackdropClick);
      this.closeButton?.addEventListener('click', this.boundOnCloseClick);

      this.updateExpansionTabStops();
      this.updateButtons();
      this.setPlayButtonState();
      this.startAutoScroll();
      this.initialized = true;
    }

    disconnectedCallback() {
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      if (this.expandedSlide) {
        this.expandedSlide.classList.remove('is-expanded');
        this.expandedSlide.setAttribute('aria-expanded', 'false');
        this.expandedSlide = null;
        this.classList.remove('is-expanded');
        document.documentElement.classList.remove('gl-reel-expanded');
      }

      this.track?.removeEventListener('wheel', this.boundOnWheel);
      this.track?.removeEventListener('pointerdown', this.boundOnPointerDown);
      this.track?.removeEventListener('scroll', this.boundOnScroll);

      window.removeEventListener('pointermove', this.boundOnPointerMove);
      window.removeEventListener('pointerup', this.boundOnPointerUp);
      window.removeEventListener('pointercancel', this.boundOnPointerUp);
      window.removeEventListener('resize', this.boundOnResize);

      this.prevButton?.removeEventListener('click', this.boundOnPrevClick);
      this.nextButton?.removeEventListener('click', this.boundOnNextClick);
      this.playButton?.removeEventListener('click', this.boundOnPlayClick);

      this.removeEventListener('click', this.boundOnSlideClick);
      this.removeEventListener('keydown', this.boundOnKeydown);
      this.removeEventListener('mouseenter', this.boundOnMouseEnter);
      this.removeEventListener('mouseleave', this.boundOnMouseLeave);
      this.removeEventListener('focusin', this.boundOnFocusIn);
      this.removeEventListener('focusout', this.boundOnFocusOut);
      this.backdrop?.removeEventListener('click', this.boundOnBackdropClick);
      this.closeButton?.removeEventListener('click', this.boundOnCloseClick);

      this.initialized = false;
    }

    setupOptions() {
      this.autoplay = this.dataset.reelAutoplay !== 'false';
      this.enableExpansion = this.dataset.reelExpand === 'true';
      this.pauseOnHover = this.dataset.reelPauseOnHover !== 'false';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.autoplay || this.reducedMotion) {
        this.userPaused = true;
      }
    }

    setupPace() {
      const paceMap = {
        'very-slow': 0.22,
        slow: 0.34,
        standard: 0.52,
        smooth: 0.72,
        faster: 0.96
      };

      const pace = (this.getAttribute('data-reel-pace') || this.dataset.reelPace || 'slow').trim().toLowerCase();

      this.speed = paceMap[pace] || paceMap.slow;
    }

    setupLoop() {
      const originalSlides = Array.from(this.track.querySelectorAll('.gl-reel__slide:not([data-reel-clone])'));

      this.track.querySelectorAll('[data-reel-clone]').forEach((clone) => clone.remove());

      if (originalSlides.length < 2) {
        this.loopPoint = 0;
        return;
      }

      originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);

        clone.dataset.reelClone = 'true';
        clone.setAttribute('aria-hidden', 'true');

        try {
          clone.inert = true;
        } catch (_error) {
          // Older browsers can ignore inert; tabindex cleanup below still prevents keyboard duplicates.
        }

        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });

        this.track.appendChild(clone);
      });

      this.updateLoopMetrics();
      this.queueMetricsUpdate();
    }

    watchMedia() {
      this.querySelectorAll('img, video').forEach((media) => {
        const isReady = media.tagName === 'IMG' ? media.complete : media.readyState >= 1;

        if (isReady) return;

        media.addEventListener('load', this.boundUpdateMetrics, { once: true });
        media.addEventListener('loadedmetadata', this.boundUpdateMetrics, { once: true });
      });
    }

    queueMetricsUpdate() {
      window.requestAnimationFrame(() => {
        this.updateLoopMetrics();
      });

      window.setTimeout(() => {
        this.updateLoopMetrics();
      }, 350);
    }

    updateLoopMetrics() {
      if (!this.track) return;

      const firstSlide = this.track.querySelector('.gl-reel__slide:not([data-reel-clone])');
      const firstClone = this.track.querySelector('[data-reel-clone]');

      if (!firstSlide || !firstClone) {
        this.loopPoint = 0;
        this.updateButtons();
        return;
      }

      this.loopPoint = Math.max(0, firstClone.offsetLeft - firstSlide.offsetLeft);
      this.updateButtons();
    }

    updateExpansionTabStops() {
      this.querySelectorAll('.gl-reel__slide--image:not([data-reel-clone]), .gl-reel__slide--video:not([data-reel-clone])').forEach((slide) => {
        if (this.enableExpansion) {
          slide.setAttribute('tabindex', '0');
          slide.setAttribute('aria-expanded', 'false');
        } else {
          slide.removeAttribute('tabindex');
          slide.removeAttribute('aria-expanded');
        }
      });
    }

    onResize() {
      this.queueMetricsUpdate();
    }

    onMouseEnter() {
      if (!this.pauseOnHover) return;

      this.hoverPaused = true;
      this.setAutoScrollingState(false);
    }

    onMouseLeave() {
      this.hoverPaused = false;
      this.resumeAt = performance.now() + 300;
    }

    onFocusIn() {
      this.focusPaused = true;
      this.setAutoScrollingState(false);
    }

    onFocusOut() {
      window.setTimeout(() => {
        this.focusPaused = this.contains(document.activeElement);
        if (!this.focusPaused) {
          this.resumeAt = performance.now() + 500;
        }
      }, 0);
    }

    setAutoScrollingState(active) {
      this.track?.classList.toggle('is-auto-scrolling', active);

      if (this.track) {
        this.track.style.scrollSnapType = active ? 'none' : '';
      }
    }

    setPlayButtonState() {
      if (!this.playButton) return;

      const isPaused = this.userPaused || !this.autoplay || this.reducedMotion;

      this.playButton.setAttribute('aria-pressed', String(!isPaused));
      this.playButton.setAttribute('aria-label', isPaused ? 'Play reel' : 'Pause reel');
      this.playButton.classList.toggle('is-paused', isPaused);
    }

    getScrollAmount() {
      const firstSlide = this.track?.querySelector('.gl-reel__slide:not([data-reel-clone])');
      if (!firstSlide) return 400;

      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);

      return firstSlide.getBoundingClientRect().width + gap;
    }

    onPrevClick() {
      this.pauseForInteraction();
      this.scrollByAmount(-1);
    }

    onNextClick() {
      this.pauseForInteraction();
      this.scrollByAmount(1);
    }

    onPlayClick() {
      if (!this.autoplay || this.reducedMotion) {
        this.autoplay = true;
      }

      this.userPaused = !this.userPaused;
      this.resumeAt = performance.now() + 150;
      this.setPlayButtonState();
      this.startAutoScroll();
    }

    scrollByAmount(direction) {
      if (!this.track) return;

      this.track.scrollBy({
        left: this.getScrollAmount() * direction,
        behavior: 'smooth'
      });
    }

    onWheel(event) {
      if (!this.track) return;

      const deltaX = event.shiftKey ? event.deltaY : event.deltaX;
      const isHorizontalIntent = Math.abs(deltaX) > Math.abs(event.deltaY) || event.shiftKey;

      if (!isHorizontalIntent) return;

      this.pauseForInteraction(2500);
      event.preventDefault();
      this.track.scrollBy({
        left: deltaX * 1.15,
        behavior: 'auto'
      });
    }

    onPointerDown(event) {
      if (!this.track) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('a, button')) return;

      this.isDragging = true;
      this.isHorizontalDrag = false;
      this.dragMoved = false;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startScrollLeft = this.track.scrollLeft;

      this.pauseForInteraction(3500);
    }

    onPointerMove(event) {
      if (!this.isDragging || !this.track) return;

      const distanceX = event.clientX - this.startX;
      const distanceY = event.clientY - this.startY;
      const absX = Math.abs(distanceX);
      const absY = Math.abs(distanceY);

      if (!this.isHorizontalDrag) {
        if (absX < 8 && absY < 8) return;

        if (absY > absX) {
          this.isDragging = false;
          this.isHorizontalDrag = false;
          this.track.classList.remove('is-dragging');
          return;
        }

        this.isHorizontalDrag = true;
        this.dragMoved = true;
        this.track.classList.add('is-dragging');

        if (this.track.setPointerCapture) {
          this.track.setPointerCapture(event.pointerId);
        }
      }

      if (absX > 6) {
        this.dragMoved = true;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      this.track.scrollLeft = this.startScrollLeft - distanceX;
    }

    onPointerUp(event) {
      if (!this.isDragging || !this.track) return;

      this.isDragging = false;
      this.isHorizontalDrag = false;
      this.track.classList.remove('is-dragging');

      if (this.track.hasPointerCapture && this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }

      window.setTimeout(() => {
        this.dragMoved = false;
      }, 0);
    }

    onSlideClick(event) {
      if (!this.enableExpansion) return;

      const slide = event.target.closest('.gl-reel__slide--image, .gl-reel__slide--video');
      if (!slide || !this.contains(slide)) return;
      if (slide.dataset.reelClone) return;
      if (event.target.closest('a, button')) return;
      if (this.dragMoved) return;

      this.pauseForInteraction(4000);

      if (slide === this.expandedSlide) {
        this.collapseSlide();
        return;
      }

      this.expandSlide(slide);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.expandedSlide) {
        this.collapseSlide();
        return;
      }

      if (event.key === 'ArrowLeft') {
        this.pauseForInteraction();
        this.scrollByAmount(-1);
        return;
      }

      if (event.key === 'ArrowRight') {
        this.pauseForInteraction();
        this.scrollByAmount(1);
        return;
      }

      if (!this.enableExpansion || (event.key !== 'Enter' && event.key !== ' ')) return;

      const slide = event.target.closest('.gl-reel__slide--image, .gl-reel__slide--video');
      if (!slide || slide.dataset.reelClone) return;

      event.preventDefault();

      if (slide === this.expandedSlide) {
        this.collapseSlide();
      } else {
        this.expandSlide(slide);
      }
    }

    expandSlide(slide) {
      if (!this.enableExpansion) return;

      if (this.expandedSlide) {
        this.expandedSlide.classList.remove('is-expanded');
        this.expandedSlide.setAttribute('aria-expanded', 'false');
      }

      this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.expandedSlide = slide;
      this.classList.add('is-expanded');
      slide.classList.add('is-expanded');
      slide.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('gl-reel-expanded');
    }

    collapseSlide() {
      if (!this.expandedSlide) return;

      this.expandedSlide.classList.remove('is-expanded');
      this.expandedSlide.setAttribute('aria-expanded', 'false');
      this.expandedSlide = null;
      this.classList.remove('is-expanded');
      document.documentElement.classList.remove('gl-reel-expanded');

      if (this.lastFocusedElement && this.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }

      this.lastFocusedElement = null;
    }

    pauseForInteraction(duration = 3000) {
      this.resumeAt = performance.now() + duration;
      this.setAutoScrollingState(false);
    }

    updateButtons() {
      if (!this.track) return;

      const canScroll = this.track.scrollWidth - this.track.clientWidth > 4;

      if (this.prevButton) this.prevButton.disabled = !canScroll;
      if (this.nextButton) this.nextButton.disabled = !canScroll;
      if (this.playButton) this.playButton.disabled = !canScroll;
    }

    startAutoScroll() {
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      if (!this.track || !this.autoplay || this.reducedMotion) {
        this.setAutoScrollingState(false);
        this.setPlayButtonState();
        return;
      }

      let last = performance.now();

      const tick = (now) => {
        if (!this.track || !this.isConnected) return;

        const delta = now - last;
        last = now;

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const canMove = maxScroll > 4 && this.loopPoint > 4;
        const paused =
          this.userPaused ||
          this.hoverPaused ||
          this.focusPaused ||
          this.isDragging ||
          this.expandedSlide ||
          document.hidden ||
          now < this.resumeAt ||
          !canMove;

        if (!paused) {
          this.setAutoScrollingState(true);

          this.autoRemainder += (this.speed * delta) / 16.6667;
          const step = Math.floor(this.autoRemainder);

          if (step > 0) {
            this.track.scrollLeft += step;
            this.autoRemainder -= step;
          }

          if (this.track.scrollLeft >= this.loopPoint) {
            this.track.scrollLeft -= this.loopPoint;
          }

          if (this.track.scrollLeft >= maxScroll - 1) {
            this.track.scrollLeft = 0;
            this.autoRemainder = 0;
          }
        } else {
          this.setAutoScrollingState(false);
        }

        this.setPlayButtonState();
        this.frame = requestAnimationFrame(tick);
      };

      this.frame = requestAnimationFrame(tick);
    }
  }

  customElements.define('cinematic-reel', CinematicReel);
}
